import type { DraftContent, OrderDraft, OrderQuote, PosPaymentInput } from './types'
import type { prepareDraftCreation } from './service'

export const emptyDraft = (): DraftContent => ({ order: { shoppingItems: [], withDelivery: false, dineIn: false, dineInMenu: false }, label: '', notes: '', tableLabel: null, lineNotes: [] })
type AfterSave = 'park' | 'submit' | 'save'
export type PendingWrite =
  | { kind: 'create'; request: ReturnType<typeof prepareDraftCreation>; after: AfterSave; confirmedQuote?: string; payment?: PosPaymentInput }
  | { kind: 'update'; id: string; revision: number; content: DraftContent; acceptMenuChanges: boolean; after: AfterSave; confirmedQuote?: string; payment?: PosPaymentInput }
  | { kind: 'submit'; id: string; revision: number; payment?: PosPaymentInput }
  | { kind: 'discard'; id: string; revision: number }
export interface EntryRecovery { content: DraftContent; draft: OrderDraft | null; pending: PendingWrite | null }
const prefix = 'pos-entry:'
let generation = 0
export const posEntryGeneration = () => generation
export const entryKey = (restaurantId: number) => `${prefix}${restaurantId}`
export function readEntry(key: string): EntryRecovery {
  try {
    const value: unknown = JSON.parse(sessionStorage.getItem(key) ?? 'null')
    if (value && typeof value === 'object' && 'content' in value) {
      const content = value.content as Partial<DraftContent> | null
      if (Array.isArray(content?.order?.shoppingItems)) return value as EntryRecovery
    }
  } catch { /* Storage may be disabled. */ }
  return { content: emptyDraft(), draft: null, pending: null }
}
export function writeEntry(key: string, value: EntryRecovery, expectedGeneration = generation): boolean {
  if (expectedGeneration !== generation) return false
  try { sessionStorage.setItem(key, JSON.stringify(value)); return true } catch { return false }
}
export function clearPosEntryRecoveries() {
  generation++
  try { Object.keys(sessionStorage).filter(key => key.startsWith(prefix)).forEach(key => sessionStorage.removeItem(key)) } catch { /* Storage may be disabled. */ }
}

/** Compare the request fields after server normalization, ignoring source and irrelevant serialization defaults. */
export function contentFingerprint(content: DraftContent) {
  return JSON.stringify({ label: content.label ?? '', notes: content.notes ?? '', tableLabel: (content.tableLabel === '' ? null : content.tableLabel) ?? null,
    delivery: content.delivery ?? null, manualDiscount: content.manualDiscount ?? null, customLines: content.customLines ?? [],
    lineNotes: [...(content.lineNotes ?? [])].sort((a, b) => a.itemIndex - b.itemIndex),
    order: { withDelivery: Boolean(content.order.withDelivery), dineIn: Boolean(content.order.dineIn), dineInMenu: Boolean(content.order.dineInMenu),
      shoppingItems: content.order.shoppingItems.map(item => ({ menuItem: item.menuItem, quantity: item.quantity ?? 1,
        variations: item.variations ?? [], variationSelections: (item.variationSelections ?? []).map(s => ({ variation: s.variation, slotKey: s.slotKey ?? '', position: s.position ?? 0 })),
        extraItems: item.extraItems ?? [] })), extraItems: content.order.extraItems ?? [] } })
}

export function quoteFingerprint(quote: OrderQuote) {
  return JSON.stringify({ currency: quote.currency, itemsTotal: quote.itemsTotal, orderTotal: quote.orderTotal ?? quote.itemsTotal, deliveryFee: quote.deliveryFee ?? 0, delivery: quote.delivery ?? null, dineInMenu: quote.dineInMenu,
    items: quote.items.map(item => ({ menuItem: item.menuItem, name: item.name, quantity: item.quantity,
      unitPrice: item.unitPrice, total: item.total, taxRate: item.taxRate,
      variations: item.variations.map(v => ({ variation: v.variation, slotKey: v.slotKey, position: v.position, name: v.name, price: v.price })) })) })
}
